// LogsTab — Story 2.6. Timeline de audit_logs do cliente.
import { formatBRDateTime } from '@control-center/shared';
import { AlertCircle, Activity } from 'lucide-react';

import { useClientAuditLogs } from '../../../hooks/use-client-audit-logs';
import { extractErrorMessage } from '../../../lib/errors';
import { PillButton } from '../PillButton';

interface LogsTabProps {
  clientId: string;
}

const ACTION_LABEL: Record<string, string> = {
  create: 'Criado',
  update: 'Atualizado',
  delete: 'Excluído',
  state_change: 'Mudança de estado',
  custom: 'Custom',
};

const ACTION_TONE: Record<string, string> = {
  create: 'bg-good/10 text-good border-good/40',
  update: 'bg-warn/10 text-warn border-warn/40',
  delete: 'bg-bad/10 text-bad border-bad/40',
  state_change: 'bg-warn/10 text-warn border-warn/40',
  custom: 'bg-muted-2/10 text-muted-2 border-muted-2/40',
};

export function LogsTab({ clientId }: LogsTabProps): JSX.Element {
  const query = useClientAuditLogs(clientId);

  if (query.isLoading) {
    return (
      <div className="flex flex-col gap-2">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            aria-hidden
            className="h-[68px] animate-pulse rounded-[8px] border border-line bg-panel-2"
          />
        ))}
      </div>
    );
  }

  if (query.isError) {
    return (
      <div className="rounded-[10px] border border-bad/30 bg-bad/5 p-4 text-[13px]">
        <div className="flex items-center gap-2 font-medium text-bad">
          <AlertCircle size={14} strokeWidth={2} aria-hidden />
          Não conseguimos carregar os logs.
        </div>
        <p className="mt-1 text-muted">{extractErrorMessage(query.error, 'Tente de novo.')}</p>
        <div className="mt-3">
          <PillButton onClick={() => void query.refetch()}>Tentar de novo</PillButton>
        </div>
      </div>
    );
  }

  const items = query.data?.items ?? [];

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <span className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-panel-3 text-muted-2">
          <Activity size={18} strokeWidth={1.5} aria-hidden />
        </span>
        <h3 className="text-[14px] font-semibold text-ink-2">Sem atividade registrada.</h3>
        <p className="mt-1 max-w-[360px] text-[12px] text-muted">
          Quando este cliente for editado, cancelado ou alterado, o histórico de auditoria aparece
          aqui.
        </p>
      </div>
    );
  }

  return (
    <ol className="flex flex-col gap-2">
      {items.map((entry) => (
        <li key={entry.id} className="rounded-[10px] border border-line bg-panel-2 p-3 text-[13px]">
          <div className="flex items-center justify-between gap-3">
            <span
              className={`inline-flex items-center gap-1 rounded-[6px] border px-2 py-[2px] text-[10px] font-medium uppercase tracking-wider ${
                ACTION_TONE[entry.action] ?? ACTION_TONE.custom
              }`}
            >
              {ACTION_LABEL[entry.action] ?? entry.action}
            </span>
            <time className="text-[11px] text-muted-2" dateTime={entry.createdAt}>
              {formatBRDateTime(new Date(entry.createdAt))}
            </time>
          </div>
          <div className="mt-2 text-ink-2">
            por <strong>{entry.user?.name ?? 'Sistema'}</strong>
            {entry.user?.email && <span className="ml-1 text-muted-2">({entry.user.email})</span>}
          </div>
          {(entry.ipAddress || entry.userAgent) && (
            <div className="mt-1 text-[11px] text-muted-2">
              {entry.ipAddress && <span className="tnum">{entry.ipAddress}</span>}
              {entry.ipAddress && entry.userAgent && <span className="mx-1">·</span>}
              {entry.userAgent && (
                <span className="truncate" title={entry.userAgent}>
                  {entry.userAgent.slice(0, 60)}
                </span>
              )}
            </div>
          )}
        </li>
      ))}
    </ol>
  );
}
